import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useState, useEffect, ChangeEvent, useCallback } from "react";

import {
  CircularProgress,
  IconButton,
  TextField,
  Tooltip
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Autocomplete } from "@material-ui/lab";

import {
  campaignVanIntegrationBeginSearch,
  campaignVanIntegrationBeginSearchVariables
} from "src/graphql/generated/campaignVanIntegrationBeginSearch";
import {
  campaignVanIntegrationForceRefreshSearch,
  campaignVanIntegrationForceRefreshSearchVariables
} from "src/graphql/generated/campaignVanIntegrationForceRefreshSearch";
import { VanProxySearchType } from "src/graphql/generated/globalTypes";
import { useFormInputStyles, useAutocompleteStyles } from "src/styles/forms";
import useMutation from "src/util/useMutation";
import useThrottle from "src/util/useThrottle";

import { REFRESH_SEARCH, START_SEARCH } from "./queries";
import { VanSearchFieldValue, TokenData } from "./types";
import { runSearch, getLabelAndIdForItem } from "./vanProxy";

// When the field is first rendered, we start in the "not loading" state. We
// don't automatically start a search session because we don't want to do a
// whole sync with VAN if the user never interacts with the component.
type DataStateNotLoading = {
  state: "notLoading";
};

// Once the user opens the dropdown, we start a search session. We stay in this
// state until the backend gives us a token to talk to the VAN proxy (which also
// indicates that the VAN proxy has a warm cache)
type DataStateLoading = {
  state: "loading";
};

// If getting a token to talk to the VAN proxy fails, we stay in this state
type DataStateFailed = {
  state: "failed";
  error: string;
};

// If there are fewer than 100 items, we load them all into memory and search
// in-memory.
type DataStateInMemory = {
  state: "inMemory";
  tokenData: TokenData;
  tokenDataValidUntil: DateTime;
  data: VanSearchFieldValue[];
};

// If there are more than 100 items, we do an as-you-type search
type DataStateRemoteSearch = {
  state: "remoteSearch";
  tokenData: TokenData;
  tokenDataValidUntil: DateTime;
};

type DataState =
  | DataStateNotLoading
  | DataStateLoading
  | DataStateFailed
  | DataStateInMemory
  | DataStateRemoteSearch;

type VanSearchFieldProps = {
  campaignId: string;
  value: VanSearchFieldValue | null;
  onChange: (evt: ChangeEvent<any>, value: VanSearchFieldValue | null) => void;
  searchType: VanProxySearchType;
  label: string;
  fullWidth?: boolean;
  style?: React.CSSProperties;
  className?: string;
  endAdornment?: React.ReactNode;
};

const IN_MEMORY_RESULT_LIMIT = 100;
const TOKEN_VALIDITY_MARGIN_SECONDS = 5 * 60;

export default function FormVanSearchField({
  campaignId,
  value,
  onChange,
  searchType,
  label,
  fullWidth = false,
  style,
  className,
  endAdornment
}: VanSearchFieldProps): JSX.Element {
  const formInputStyles = useFormInputStyles();
  const autocompleteStyles = useAutocompleteStyles();
  const snackbar = useSnackbar();

  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dataState, setDataState] = useState<DataState>({
    state: "notLoading"
  });
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState<
    | null
    | { error: null; results: VanSearchFieldValue[]; warning?: string }
    | { error: string }
  >(null);
  const throttledSearchText = useThrottle(searchText, 500);

  const [campaignVanIntegrationBeginSearch] = useMutation<
    campaignVanIntegrationBeginSearch,
    campaignVanIntegrationBeginSearchVariables
  >(START_SEARCH);

  const [campaignVanIntegrationRefreshSearch] = useMutation<
    campaignVanIntegrationForceRefreshSearch,
    campaignVanIntegrationForceRefreshSearchVariables
  >(REFRESH_SEARCH);

  /**
   * Requests that the backend ensure the VAN proxy has a warm cache, and
   * get a token to talk to it.
   */
  const initializeSearchSession = useCallback(async (): Promise<TokenData> => {
    // Start the search session -- this refreshes data from VAN (if the data
    // hasn't been refreshed recently) and returns a token to talk to the VAN
    // proxy
    const response = await campaignVanIntegrationBeginSearch({
      variables: {
        campaignId,
        type: searchType
      }
    });

    if (response.errors) {
      throw new Error(
        response.errors[0]?.message || "Failed to load search data from NGP VAN"
      );
    }

    return response.data!.campaignVanIntegrationBeginSearch;
  }, [campaignId, campaignVanIntegrationBeginSearch, searchType]);

  /**
   * Force-refreshes the VAN proxy data
   */
  async function refreshData() {
    setRefreshing(true);

    try {
      // make sure we have a valid token
      const tokenData = tokenIsValid()
        ? (dataState as DataStateInMemory | DataStateRemoteSearch).tokenData
        : await initializeSearchSession();

      const { errors, data } = await campaignVanIntegrationRefreshSearch({
        variables: {
          campaignId: campaignId,
          type: searchType
        }
      });

      if (errors || !data?.campaignVanIntegrationForceRefreshSearch.success) {
        console.error("Error force-refreshing data", { errors, data });
        throw new Error("Failed to refresh data from NGP VAN");
      }

      const newDataState = await loadSearchData(
        {
          ...tokenData,
          lastUpdated:
            data.campaignVanIntegrationForceRefreshSearch.lastUpdated!,
          totalCount: data.campaignVanIntegrationForceRefreshSearch.totalCount!
        },
        searchType
      );
      setDataState(newDataState);
    } catch (e) {
      console.error(e);

      snackbar.enqueueSnackbar("Failed to refresh data from NGP VAN", {
        variant: "error"
      });
    }

    setRefreshing(false);
  }

  /**
   * Returns whether our VAN proxy token is still valid
   */
  function tokenIsValid(): boolean {
    if (dataState.state !== "inMemory" && dataState.state !== "remoteSearch") {
      return false;
    }

    return dataState.tokenDataValidUntil > DateTime.now();
  }

  // This useEffect block is responsible for initializing the search session.
  // It's run when the dropdown is opened for the first time, or re-opened after
  // the token expires.
  useEffect(() => {
    if (dataState.state !== "loading") {
      return;
    }

    // Keep track of whether this useEffect has been cancelled
    let active = true;

    (async () => {
      const tokenData = await initializeSearchSession();
      const dataState = await loadSearchData(tokenData, searchType);

      if (!active) {
        return;
      }

      setDataState(dataState);
    })().catch(err => {
      console.error(err);

      if (!active) {
        return;
      }

      setDataState({
        state: "failed",
        error: "Error loading NGP VAN data"
      });
    });

    return () => {
      active = false;
    };
  }, [dataState.state, initializeSearchSession, searchType]);

  // This useEffect is responsible for doing the actual search, if we're searching
  // >100 items so we're doing the search server-side.
  //
  // It's trigged by a change in throttledSearchText
  useEffect(() => {
    if (
      dataState.state !== "remoteSearch" ||
      !throttledSearchText ||
      throttledSearchText.length < 3
    ) {
      setSearchResults(null);
      return;
    }

    let active = true;

    void (async () => {
      const results = await runSearch(dataState.tokenData, {
        query: throttledSearchText
      });

      if (!active) {
        // This useEffect() was cancelled before we got the response.
        return;
      }

      const data = results.data!;

      let warning: string | undefined;
      if (data.results.length < data.totalResultCount.count) {
        if (data.totalResultCount.type === "exact") {
          warning = `${
            data.totalResultCount.count - data.results.length
          } more results not shown...`;
        } else {
          warning = `Over ${
            data.totalResultCount.count - data.results.length
          } more results not shown...`;
        }
      }

      setSearchResults({
        error: null,
        results: data.results.map(result =>
          getLabelAndIdForItem(searchType, result)
        ),
        warning
      });
    })().catch(err => {
      console.error(err);

      if (!active) {
        // This useEffect() was cancelled before we got the response.
        return;
      }

      setSearchResults({
        error: "Error loading NGP VAN data"
      });
    });

    return () => {
      active = false;
    };
  }, [throttledSearchText, dataState, searchType]);

  // Generate the list of drop-down options. If we're in a loading or error state,
  // we render a single disabled option to display that status
  //
  // We also customize optionMatcher, which is used by the Autocomplete
  // component to determine which option is currently selected (for keyboard
  // navigation when you open the dropdown). Autocomplete prints warnings if
  // no option matches the current value, so if we're only rendering a
  // pseudo-option (to show a loading or error state), we override the
  // optionMatcher to always return true (making that pseudo-option selected).
  let options: VanSearchFieldValue[] = [
    { id: -1, label: "Loading", disabled: true }
  ];
  let optionMatcher: (
    option: VanSearchFieldValue,
    value: VanSearchFieldValue
  ) => boolean = () => true;

  if (dataState.state === "failed") {
    options = [{ id: -1, label: dataState.error, disabled: true }];
  }

  // if we're in the "inMemory" state, we've loaded all the items and will
  // let the Autocomplete component do the actual filtering for us
  if (dataState.state === "inMemory") {
    options = dataState.data;
    optionMatcher = (option, value) => option.id === value.id;
  }

  // If we're in the "remoteSearch" state, we're doing the search on the server
  // side. Make sure the user has entered at least 3 characters, and then
  // show the server-generated results.
  if (dataState.state === "remoteSearch") {
    if (searchText.length < 3) {
      options = [
        {
          id: -1,
          label: "Enter 3 or more characters to search...",
          disabled: true
        }
      ];
    } else if (searchResults) {
      if (searchResults.error != null) {
        options = [{ id: -1, label: searchResults.error, disabled: true }];
      } else {
        options = searchResults.results;
        optionMatcher = (option, value) => option.id === value.id;

        if (searchResults.warning) {
          options = options.concat([
            {
              id: -1,
              label: searchResults.warning,
              disabled: true
            }
          ]);
        }
      }
    }
  }

  // Helper to render the adornment inside the text box that indicates the
  // state of the data (loading or refreshable)
  const renderDataAdornment = () => {
    if (dataState.state === "loading" || refreshing) {
      return <CircularProgress color="inherit" size={20} />;
    }

    let tooltipMessage = "Load options from NGP VAN";
    let clickHandler = () => setDataState({ state: "loading" });
    if (dataState.state === "inMemory" || dataState.state === "remoteSearch") {
      // Already loaded; this becomes a force-refresh button
      tooltipMessage = `Synced with NGP VAN ${DateTime.fromMillis(
        dataState.tokenData.lastUpdated
      ).toRelative()}. Click to refresh.`;
      clickHandler = refreshData;
    }

    return (
      <Tooltip title={tooltipMessage}>
        <IconButton onClick={clickHandler}>
          <RefreshIcon style={{ width: 20, height: 20 }} />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <div className={autocompleteStyles.wrapper}>
      <div className={autocompleteStyles.autocompleteContainer}>
        <Autocomplete
          loading={dataState.state === "loading"}
          options={options}
          getOptionSelected={optionMatcher}
          getOptionLabel={option => option.label}
          getOptionDisabled={option => !!option.disabled}
          open={open}
          onOpen={() => {
            setOpen(true);
            if (dataState.state === "notLoading" || !tokenIsValid()) {
              setDataState({
                state: "loading"
              });
            }
          }}
          onClose={() => {
            setOpen(false);
          }}
          // These callbacks are for when a new item is selected from the dropdown
          value={value}
          onChange={onChange}
          // This callback is for as the user types into the search box
          onInputChange={(event, newInputValue) => {
            setSearchText(newInputValue);
          }}
          // We only do client-side search in the "inMemory" state -- otherwise,
          // the Autocomplete component would filter out our loading/error options
          filterOptions={
            dataState.state === "inMemory" ? undefined : opts => opts
          }
          // Style properties
          fullWidth={fullWidth}
          style={style}
          className={className}
          classes={{
            inputRoot: `${formInputStyles.input} ${autocompleteStyles.inputRoot}`
          }}
          // Rendering for the text input
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
                classes: {
                  root: `${formInputStyles.inputLabel} ${autocompleteStyles.inputLabel}`
                }
              }}
            />
          )}
        />
      </div>

      <div className={autocompleteStyles.dataAdornment}>
        {renderDataAdornment()}
      </div>

      {endAdornment ? endAdornment : null}
    </div>
  );
}

/**
 * Determine whether we should do server-side or in-memory searching, and
 * load data for in-memory searching
 */
async function loadSearchData(
  tokenData: TokenData,
  searchType: VanProxySearchType
): Promise<DataState> {
  // Determine whether there's >100 results, in which case we should do
  // an as-you-type search on the server-side, or not, in which case we
  // load all the items into memory and search in-memory.
  if (tokenData.totalCount > IN_MEMORY_RESULT_LIMIT) {
    // Large data set; do a server-side search
    return {
      state: "remoteSearch",
      tokenData: tokenData,
      tokenDataValidUntil: DateTime.now()
        .plus({
          seconds: tokenData.tokenExpiresInSeconds
        })
        .minus({ seconds: TOKEN_VALIDITY_MARGIN_SECONDS })
    };
  }

  // The data set is small enough that we can just load it and search it
  // in-memory
  const dataSet = await runSearch<VanProxySearchType>(tokenData, {
    limit: IN_MEMORY_RESULT_LIMIT
  });

  return {
    state: "inMemory",
    tokenData: tokenData,
    tokenDataValidUntil: DateTime.now()
      .plus({
        seconds: tokenData.tokenExpiresInSeconds
      })
      .minus({ seconds: TOKEN_VALIDITY_MARGIN_SECONDS }),
    data: dataSet.data!.results.map(result =>
      getLabelAndIdForItem(searchType, result)
    )
  };
}

export type { VanSearchFieldValue };
